import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { TrendingUp, Activity, ChartNoAxesCombined, ChevronRight, ChevronLeft, PieChart, Coins, ShieldCheck } from 'lucide-react';
import s from './NovaDashboard.module.sass';
import { useAppContext } from '../../../app-core/AppContext';
import { useNovaPortfolio } from '../useNovaPortfolio';
import NovaChart from '../NovaChart';
import NovaWithdrawPanel from './NovaWithdrawPanel';
import NovaTrending from '../NovaTrending';
import NovaNews from '../NovaNews';
import FlagIcon from '../../../components/FlagIcon/FlagIcon';

/**
 * NovaDashboard — дашборд в один экран.
 * Приветствие и стат-полоса живут в хедере (NovaHeader); здесь только
 * рабочая область: график портфеля с активами слева, панель операций справа.
 * На мобильных стата дублируется компактной сеткой сверху.
 */
export default function NovaDashboard() {
    const {
        nameUser, confirmedNameUser,
        selectedCrypto, cryptoOptions = [],
        bnbBalanca,
        siteSettings = {},
        setShowExactMenu,
        safeT,
    } = useAppContext();

    const {
        currencyFormat, displayAmount, totalValueText,
        growthData, todayPnL, resolveInitialInvestment,
        formatNumber, formatCrypto, showCodeModal,
    } = useNovaPortfolio();

    const t = typeof safeT === 'function' ? safeT : (_k, f) => f;
    const [selectedRange, setSelectedRange] = useState('24H');

    // ── Данные графика (как в оригинальном Dashbroad)
    const generateChartData = (startAmount, endAmount, numPoints) => {
        if (startAmount === 0 && endAmount === 0) {
            return Array.from({ length: numPoints }, (_, i) => ({ name: i === 0 ? '0' : 'Session', balance: 0 }));
        }
        const temp = [];
        let b = 1000;
        for (let i = 0; i < numPoints; i++) { b += Math.random() * 6500 - 2500; if (b < 0) b = 100; temp.push(b); }
        const minT = Math.min(...temp), maxT = Math.max(...temp);
        const range = maxT - minT || 1;
        const target = endAmount - startAmount;
        const data = temp.map((v, i) => ({ name: i === 0 ? '0' : 'Session', balance: parseFloat((startAmount + ((v - minT) / range) * target).toFixed(2)) }));
        if (data.length) { data[0].balance = startAmount; data[data.length - 1].balance = endAmount; }
        return data;
    };

    const chartAmounts = useMemo(() => {
        if (showCodeModal) return { start: 0, end: 0 };
        const currentBalance = parseFloat(displayAmount.toString().replace(/,/g, ''));
        if (currentBalance === 0 || isNaN(currentBalance)) return { start: 0, end: 0 };
        return { start: resolveInitialInvestment(currentBalance), end: currentBalance };
    }, [displayAmount, showCodeModal, resolveInitialInvestment]);

    const chartData = useMemo(() => {
        if (showCodeModal) return Array.from({ length: 100 }, (_, i) => ({ name: i === 0 ? '0' : 'Session', balance: 0 }));
        return generateChartData(chartAmounts.start, chartAmounts.end, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartAmounts.start, chartAmounts.end, confirmedNameUser, showCodeModal]);

    // ── Активы
    const otherCryptoRef = useRef(null);
    const selectedCryptoData = cryptoOptions.find(c => c.symbol === selectedCrypto);
    const otherCrypto = useMemo(() => {
        if (!otherCryptoRef.current || otherCryptoRef.current.selectedCrypto !== selectedCrypto) {
            const others = cryptoOptions.filter(c => c.symbol !== selectedCrypto);
            const rnd = others[Math.floor(Math.random() * others.length)];
            otherCryptoRef.current = { selectedCrypto, crypto: rnd || others[0] || cryptoOptions[0] };
        }
        return otherCryptoRef.current.crypto;
    }, [selectedCrypto, cryptoOptions]);

    const decimals = siteSettings?.cryptoDecimalPlaces || 5;
    const portfolioAssets = [
        {
            name: selectedCryptoData?.name || 'Ethereum',
            symbol: selectedCrypto,
            icon: selectedCryptoData?.icon,
            balance: showCodeModal ? formatCrypto(0, decimals) : formatCrypto(bnbBalanca || 0, decimals),
            value: showCodeModal ? 0 : displayAmount,
            share: showCodeModal || displayAmount === 0 ? '0%' : '100%',
            change: showCodeModal ? '0%' : (displayAmount === 0 ? '0%' : growthData.value),
            changePositive: showCodeModal ? null : (growthData.isZero ? null : growthData.positive),
        },
        {
            name: otherCrypto?.name || 'Bitcoin',
            symbol: otherCrypto?.symbol || 'BTC',
            icon: otherCrypto?.icon,
            balance: formatCrypto(0, decimals),
            value: 0,
            share: '0%',
            change: '0%',
            changePositive: null,
        },
    ];

    const deltaClass = (positive) => positive === null ? s.flat : positive ? s.up : s.down;

    // Стата для hero-секции (ключи — как в оригинальном Dashbroad)
    const heroStats = [
        { Icon: TrendingUp, label: t('dashboard.todayPnL', 'P&L Сегодня'), value: showCodeModal ? `${currencyFormat} 0.00` : todayPnL.value, delta: showCodeModal ? '0%' : todayPnL.change, positive: showCodeModal ? null : todayPnL.positive, badge: true },
        { Icon: Activity, label: t('dashboard.activePositions', 'Активные Позиции'), value: '0', delta: t('dashboard.noActiveTradingSessions', 'Нет активных торговых сессий'), positive: null, badge: false },
        { Icon: ChartNoAxesCombined, label: t('dashboard.portfolioGrowth', 'Рост Портфолио'), value: showCodeModal ? '0%' : growthData.value, delta: t('dashboard.thisMonth', 'В этом месяце'), positive: showCodeModal ? null : (growthData.isZero ? null : growthData.positive), badge: true },
    ];

    const arrow = (positive) => positive === null ? '' : positive ? '↑ ' : '↓ ';

    // Имя брокера — из настроек (как в оригинальных модалах App.js)
    const brokerName = siteSettings.brokerName || siteSettings.defaultUserName || '—';

    // Таймлайн: при нехватке ширины скроллится; стрелки и затухание краёв
    // появляются только когда есть скрытая часть
    const segRef = useRef(null);
    const [segEdges, setSegEdges] = useState({ left: false, right: false });
    const updateSegEdges = () => {
        const el = segRef.current;
        if (!el) return;
        setSegEdges({
            left: el.scrollLeft > 2,
            right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
        });
    };
    useLayoutEffect(() => {
        updateSegEdges();
        const el = segRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => requestAnimationFrame(updateSegEdges));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const scrollSeg = (dir) => {
        segRef.current?.scrollBy({ left: dir * 90, behavior: 'smooth' });
    };

    // Копейки в сумме — отдельно, мельче и приподняты (финтех-стиль)
    const [totalInt, totalCents] = useMemo(() => {
        const str = String(totalValueText ?? '');
        const m = str.match(/^(.*)\.(\d{1,2})$/);
        return m ? [m[1], m[2]] : [str, null];
    }, [totalValueText]);

    // Имя клиента (из админки) — шрифт ужимается только когда реально
    // не влезает в доступную ширину (до статы справа)
    const clientName = nameUser || '—';
    // Зелёный щит «брокер верифицирован» перед именем: только когда показано
    // именно имя брокера (defaultUserName задан и совпадает с текущим именем —
    // это состояние включает галочка «Показать личность» в админке).
    // Рядом с именем обычного клиента щит не появляется
    const brokerDefaultName = (siteSettings?.defaultUserName || '').trim();
    const showBrokerVerified = Boolean(brokerDefaultName) && clientName.trim() === brokerDefaultName;
    const clientRef = useRef(null);
    useLayoutEffect(() => {
        const el = clientRef.current;
        if (!el) return;
        const BASE = 30;
        const MIN = 13;
        const fit = () => {
            // Замер на базовом кегле в одну строку: scrollWidth пропорционален
            // font-size, поэтому целевой размер считаем математикой
            el.style.whiteSpace = 'nowrap';
            el.style.fontSize = `${BASE}px`;
            // Доступная ширина = родитель минус соседи (щит «верифицирован»)
            // и минус flex-gap между ними, иначе имя со щитом обрезается
            const parent = el.parentElement;
            let avail = parent ? parent.clientWidth : 0;
            if (parent) {
                const kids = Array.from(parent.children);
                kids.forEach((child) => {
                    if (child !== el) avail -= child.offsetWidth;
                });
                const cs = getComputedStyle(parent);
                const gap = parseFloat(cs.columnGap || cs.gap) || 0;
                avail -= gap * Math.max(0, kids.length - 1);
            }
            const needed = el.scrollWidth;
            const raw = (avail > 0 && needed > avail) ? BASE * avail / needed : BASE;
            // Ниже MIN не ужимаем — читаемость важнее; вместо этого
            // разрешаем перенос на вторую строку
            el.style.fontSize = `${Math.max(MIN, Math.floor(raw))}px`;
            el.style.whiteSpace = raw < MIN ? 'normal' : 'nowrap';
        };
        fit();
        // ResizeObserver ловит любое изменение доступной ширины (не только
        // ресайз окна): рост статы справа при выборе тарифа, смену языка и т.п.
        // Пересчёт откладываем на следующий кадр, чтобы изменение шрифта внутри
        // колбэка не породило новое уведомление в том же цикле (ошибка
        // «ResizeObserver loop completed with undelivered notifications»)
        let ro;
        let rafId = 0;
        const scheduleFit = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(fit);
        };
        if (typeof ResizeObserver !== 'undefined' && el.parentElement) {
            ro = new ResizeObserver(scheduleFit);
            ro.observe(el.parentElement);
        }
        window.addEventListener('resize', scheduleFit);
        return () => {
            window.removeEventListener('resize', scheduleFit);
            cancelAnimationFrame(rafId);
            if (ro) ro.disconnect();
        };
    }, [clientName, showBrokerVerified]);

    return (
        <div className={s.page}>
            {siteSettings?.showWelcomeBlock !== false && (
                <section className={s.heroWrap}>
                    <span className={s.sectionTab}>
                        {t('dashboard.welcomeBack', 'С возвращением')}, {brokerName}!
                    </span>
                    <div className={s.hero}>
                        <div className={s.heroLeft}>
                            <span className={s.heroLabel}>{t('dashboard.workingWithClient', 'Активный профиль клиента:')}</span>
                            <div className={s.heroBalanceRow}>
                                {showBrokerVerified && (
                                    <span className={s.heroVerified} title={t('dashboard.brokerVerified', 'Брокер верифицирован')}>
                                        <ShieldCheck size={22} strokeWidth={2.2} aria-hidden="true" />
                                    </span>
                                )}
                                <span
                                    ref={clientRef}
                                    className={s.heroClient}
                                >
                                    {clientName}
                                </span>
                            </div>
                        </div>
                        <div className={s.heroStats}>
                            {heroStats.map((stat, i) => (
                                <div key={i} className={s.heroStat}>
                                    <span className={`${s.heroStatIcon} ${deltaClass(stat.positive)}`}>
                                        <stat.Icon size={15} strokeWidth={1.9} aria-hidden="true" />
                                    </span>
                                    <span className={s.heroStatBody}>
                                        <span className={s.heroStatLabel}>{stat.label}</span>
                                        <span className={s.heroStatValueRow}>
                                            <span className={s.heroStatValue}>{stat.value}</span>
                                            {stat.badge ? (
                                                <span className={`${s.heroStatBadge} ${stat.positive === null ? s.flatBadge : stat.positive ? s.upBadge : s.downBadge}`}>
                                                    {arrow(stat.positive)}{stat.delta}
                                                </span>
                                            ) : (
                                                <span className={`${s.heroStatDelta} ${deltaClass(stat.positive)}`}>{stat.delta}</span>
                                            )}
                                        </span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <div className={s.grid}>
                {/* ── Портфель */}
                <section className={s.portfolioWrap} aria-label={t('dashboard.portfolioOverview', 'Обзор портфеля')}>
                    <span className={s.sectionTab}>
                        <PieChart size={13} strokeWidth={2.1} aria-hidden="true" />
                        {t('dashboard.portfolioOverview', 'Обзор Портфолио')}
                        <span className={s.liveDot} aria-hidden="true" />
                    </span>
                    <div className={s.portfolio}>
                    <div className={s.portfolioHead}>
                        <div className={s.valueBlock}>
                            {/* Флаг валюты из конфига */}
                            <FlagIcon siteSettings={siteSettings} className={s.valueFlagImg} alt="" />
                            <div className={s.valueMain}>
                                <div className={s.valueRow}>
                                    <span className={s.totalValue}>
                                        <span className={s.totalCurrency}>{currencyFormat}</span>
                                        <span className={s.totalAmount}>
                                            {totalInt}
                                            {totalCents !== null && (
                                                <span className={s.totalCents}>{totalCents}</span>
                                            )}
                                        </span>
                                    </span>
                                    {/* Бейдж в строке суммы — прячется, когда карточке тесно */}
                                    <span className={`${s.valueBadge} ${s.badgeInline} ${showCodeModal ? s.flatBadge : todayPnL.positive === null ? s.flatBadge : todayPnL.positive ? s.upBadge : s.downBadge}`}>
                                        {showCodeModal ? '0%' : `${arrow(todayPnL.positive)}${todayPnL.change}`}
                                    </span>
                                </div>
                                <span className={`${s.valueDelta} ${showCodeModal ? s.flat : deltaClass(todayPnL.positive)}`}>
                                    {/* Вынесено из потока: длина подписи не раздувает колонку баланса,
                                        текст свободно выезжает под таймлайн */}
                                    <span className={s.deltaFlow}>
                                        {/* Дублёр бейджа для узкой карточки — встаёт слева от P&L */}
                                        <span className={`${s.valueBadge} ${s.badgeWrapped} ${showCodeModal ? s.flatBadge : todayPnL.positive === null ? s.flatBadge : todayPnL.positive ? s.upBadge : s.downBadge}`}>
                                            {showCodeModal ? '0%' : `${arrow(todayPnL.positive)}${todayPnL.change}`}
                                        </span>
                                        <span className={s.deltaText}>
                                            {showCodeModal ? `${currencyFormat} 0.00` : todayPnL.value} · {t('dashboard.todayPnL', 'P&L Сегодня')}
                                        </span>
                                    </span>
                                </span>
                            </div>
                        </div>
                        <div className={s.segmentedWrap}>
                            {segEdges.left && (
                                <button type="button" className={s.segArrow} onClick={() => scrollSeg(-1)} aria-label="←">
                                    <ChevronLeft size={13} strokeWidth={2} aria-hidden="true" />
                                </button>
                            )}
                            <div
                                ref={segRef}
                                className={`${s.segScroll} ${segEdges.left ? s.segFadeL : ''} ${segEdges.right ? s.segFadeR : ''}`}
                                onScroll={updateSegEdges}
                            >
                            <div className={s.segmented} role="tablist" aria-label="Time range">
                            {['1H', '24H', '1W', '1M', '1Y'].map(v => (
                                <button
                                    key={v}
                                    role="tab"
                                    aria-selected={selectedRange === v}
                                    className={`${s.segBtn} ${selectedRange === v ? s.segActive : ''}`}
                                    onClick={() => setSelectedRange(v)}
                                >
                                    {v}
                                </button>
                            ))}
                            </div>
                            </div>
                            {segEdges.right && (
                                <button type="button" className={s.segArrow} onClick={() => scrollSeg(1)} aria-label="→">
                                    <ChevronRight size={13} strokeWidth={2} aria-hidden="true" />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className={s.chartBox}>
                        <NovaChart
                            data={chartData}
                            currencyFormat={currencyFormat}
                            tooltipLabels={{
                                balance: t('dashboard.chartTooltip.balance', 'Баланс'),
                                change: t('dashboard.chartTooltip.change', 'Изменение'),
                                initial: t('dashboard.chartTooltip.initial', 'Старт'),
                            }}
                        />
                    </div>

                    <div className={s.assets}>
                        <div className={s.assetsHead}>
                            <span className={s.assetsTab}>
                                <Coins size={13} strokeWidth={2.1} aria-hidden="true" />
                                {t('dashboard.yourAssets', 'Ваши Активы')}
                            </span>
                            <button
                                type="button"
                                className={s.viewAllBtn}
                                onClick={() => setShowExactMenu && setShowExactMenu('wallet')}
                            >
                                {t('dashboard.viewAll', 'Посмотреть все')}
                                <ChevronRight size={13} strokeWidth={2} aria-hidden="true" />
                            </button>
                        </div>
                        <ul className={s.assetList}>
                            {/* Подписи колонок — ключи из wallet/tradingDashboard */}
                            <li className={s.assetColsRow} aria-hidden="true">
                                <span>{t('wallet.tableHeaders.currency', 'Валюта')}</span>
                                <span className={s.assetCol}>{t('tradingDashboard.form.amountLabel', 'Количество')}</span>
                                <span className={s.assetCol}>{t('wallet.tableHeaders.amount', 'Сумма')}</span>
                                <span className={s.assetCol}>{t('tradingDashboard.stats.change24h', 'Изменение 24ч')}</span>
                            </li>
                            {portfolioAssets.map((asset, i) => {
                                const isZero = !parseFloat(asset.balance) && !asset.value;
                                return (
                                <li
                                    key={i}
                                    className={`${s.assetItem} ${isZero ? s.assetItemZero : ''}`}
                                    onClick={() => setShowExactMenu && setShowExactMenu('wallet')}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && setShowExactMenu) setShowExactMenu('wallet'); }}
                                >
                                    <span className={s.assetIdent}>
                                        {asset.icon
                                            ? <img src={asset.icon} alt="" className={s.assetIcon} />
                                            : <span className={s.assetIconFallback}>{asset.symbol?.charAt(0)}</span>}
                                        <span className={s.assetNames}>
                                            <span className={s.assetName}>{asset.name}</span>
                                            <span className={s.assetSymbol}>
                                                {asset.symbol}
                                                <span className={s.assetShareBadge}>{asset.share}</span>
                                            </span>
                                        </span>
                                    </span>
                                    <span className={s.assetBalance}>
                                        {asset.balance}
                                        <span className={s.assetUnit}>{asset.symbol}</span>
                                    </span>
                                    <span className={s.assetValue}>
                                        {currencyFormat} {formatNumber(typeof asset.value === 'number' ? asset.value : parseFloat(String(asset.value).replace(/,/g, '')) || 0, siteSettings?.numberFormat)}
                                    </span>
                                    <span className={s.assetChangeCell}>
                                        <span className={`${s.assetChangeBadge} ${asset.changePositive === null ? s.flatBadge : asset.changePositive ? s.upBadge : s.downBadge}`}>
                                            {arrow(asset.changePositive)}{asset.change}
                                        </span>
                                    </span>
                                </li>
                                );
                            })}
                        </ul>
                    </div>
                    </div>
                </section>

                {/* ── Панель операций */}
                <section className={s.trading} aria-label={t('dashboard.withdrawalDetails', 'Детали вывода')}>
                    <NovaWithdrawPanel availableMoney={showCodeModal ? 0 : displayAmount} />
                </section>

                {/* ── Нижний ряд «кирпичной кладки» (только на высоких экранах):
                     узкие тренды слева под балансом, широкие новости под выводом */}
                <div className={s.brickLeft}>
                    <NovaTrending />
                </div>
                <div className={s.brickRight}>
                    <NovaNews />
                </div>
            </div>
        </div>
    );
}
